import React, { useMemo } from "react";
import styled, { useTheme } from "styled-components";
import {
  Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  FiBox, FiCalendar, FiChevronRight, FiHome, FiInfo, FiLayers, FiPackage, FiTool, FiTruck, FiZap,
} from "react-icons/fi";
import { Sparkline } from "../../activity-logs/components/primitives";
import {
  Group, Section, SectionHeader, Skeleton, Widget, WidgetGrid, tokens,
} from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import {
  Bento, BentoTile, CompositionWidget, HeaderLink, Notice, PeriodMap, PeriodStats, StorageBar, TopHeadline,
} from "../../creditos/styles";
import { MoneyValue } from "../../creditos/components/parts";
import { ChartTooltip } from "../../creditos/components/charts";
import { formatCompact, formatCurrency, formatPercent } from "../../creditos/dashboardData";
import { hairline, surface } from "../../../styles/designTokens";
import { SERIES, buildReformaDashboard, formatRate, formatTimes } from "./reformaData";

/**
 * Dashboard da Reforma Tributária, com as mesmas peças e o mesmo ritmo de Créditos:
 * KPIs, evolução + composição, mapa + mosaico e cartões de destaque. Vermelho marca
 * imposto a mais, mesmo quando o número é o destaque do card.
 */

export const signed = (v) => `${v > 0 ? "+" : ""}${formatCompact(v)}`;
export const directionColor = (v) => (v > 0 ? IOS.red : IOS.green);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const spark = { width: 80, height: 24, points: 60 };

const ACCOUNT_ICONS = { "01": FiPackage, "02": FiBox, "03": FiTool, "04": FiZap, "05": FiHome, "07": FiTruck, "11": FiLayers, "13": FiHome };
const ACCOUNT_COLORS = [IOS.blue, IOS.green, IOS.orange, IOS.purple, IOS.teal, IOS.gray];

// O mesmo mapa de Créditos, com um ano por coluna e o valor dentro da célula
const TransitionMap = styled(PeriodMap)`
  grid-template-columns: 74px repeat(8, minmax(0, 1fr));
  min-width: 540px;

  .cell span.txt {
    position: relative;
    z-index: 1;
    font-size: 0.94em;
    font-weight: 500;
    white-space: nowrap;
    color: ${({ theme }) => theme.text};
  }
  .total { display: flex; align-items: center; justify-content: center; height: 30px; font-weight: 600; white-space: nowrap; color: ${({ theme }) => theme.text}; }
  .year { white-space: nowrap; }
`;

// Em tela estreita o mapa rola, para os valores continuarem legíveis
const MapScroll = styled.div`
  overflow-x: auto;
  margin-bottom: 14px;
  scrollbar-width: thin;
`;

const hatch = (theme) => `repeating-linear-gradient(-45deg, ${tokens.label3(theme)} 0 1.5px, transparent 1.5px 4px)`;

/* Nome, gráfico e valor nas mesmas colunas, para o olho correr na vertical */
const PLOT_COLUMNS = "minmax(120px, 168px) minmax(0, 1fr) 104px";

export const PlotHead = styled.div`
  display: grid;
  grid-template-columns: ${PLOT_COLUMNS};
  gap: 14px;
  margin-top: 16px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  .dir { display: flex; justify-content: space-between; }
  .end { text-align: right; }
`;

export const PlotRow = styled.div`
  display: grid;
  grid-template-columns: ${PLOT_COLUMNS};
  align-items: center;
  gap: 14px;
  min-height: 50px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .name { min-width: 0; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .name small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 400; letter-spacing: 0; color: ${({ theme }) => tokens.label2(theme)}; }
  .plot { position: relative; align-self: stretch; }
  /* O eixo do zero atravessa as linhas e forma uma só régua */
  .plot::after { content: ""; position: absolute; left: 50%; top: 0; bottom: -1px; width: 1px; background: ${({ theme }) => hairline(theme, true)}; }
  .plot i { position: absolute; top: 50%; height: 4px; margin-top: -2px; min-width: 3px; }
  .plot i.up { border-radius: 0 2px 2px 0; }
  .plot i.down { border-radius: 2px 0 0 2px; }
  .num { text-align: right; white-space: nowrap; font-size: 0.94em; font-weight: 600; letter-spacing: -0.025em; }
`;

const WorkshopRate = styled.div`
  display: grid;
  grid-template-columns: ${PLOT_COLUMNS};
  align-items: center;
  gap: 14px;
  margin-top: auto;
  padding-top: 14px;

  .name { min-width: 0; font-size: 0.86em; font-weight: 500; letter-spacing: -0.012em; }
  .name small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; line-height: 1.35; color: ${({ theme }) => tokens.label2(theme)}; }
  .num { text-align: right; white-space: nowrap; font-size: 0.94em; font-weight: 600; letter-spacing: -0.025em; }
  .num small { display: block; margin-top: 2px; font-size: 0.78em; font-weight: 400; letter-spacing: 0; color: ${({ theme }) => tokens.label2(theme)}; }
`;

// Dois pontos em uma régua: de onde a alíquota sai e aonde ela chega
const Dumbbell = styled.div`
  position: relative;
  height: 40px;
  .rail { position: absolute; left: 0; right: 0; bottom: 8px; height: 1px; background: ${({ theme }) => hairline(theme, true)}; }
  .span { position: absolute; bottom: 7px; height: 3px; border-radius: 2px; }
  .pt { position: absolute; bottom: 5px; width: 7px; height: 7px; margin-left: -3.5px; border-radius: 50%; box-shadow: 0 0 0 2.5px ${({ theme }) => tokens.solid(theme)}; }
  .lb { position: absolute; bottom: 17px; transform: translateX(-50%); font-size: 0.74em; font-weight: 500; white-space: nowrap; }
  .lb.from { color: ${({ theme }) => tokens.label2(theme)}; font-weight: 400; }
`;

const SourceTrack = styled.div`
  display: flex;
  gap: 2px;
  height: 6px;
  margin-top: 16px;
  border-radius: 3px;
  overflow: hidden;
  span { display: block; min-width: 3px; }
  .real { background: ${IOS.green}; opacity: 0.8; }
  .est { background-image: ${({ theme }) => hatch(theme)}; background-color: ${({ theme }) => surface(theme, 1)}; }
`;

const SourceCaps = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
  font-size: 0.76em;
  color: ${({ theme }) => tokens.label2(theme)};
  span:last-child { text-align: right; }
  b { font-weight: 600; color: ${({ theme }) => theme.text}; }
`;

// Premissa em hachura e dado do SPED como marca sólida, na mesma linguagem da barra acima
const Bullet = styled.div`
  position: relative;
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => surface(theme, 2)};
  i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 3px; background-image: ${({ theme }) => hatch(theme)}; }
  b { position: absolute; top: -4px; bottom: -4px; width: 2px; margin-left: -1px; border-radius: 1px; background: ${IOS.green}; }
`;

const BulletRow = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 168px) minmax(0, 1fr) 62px;
  align-items: center;
  gap: 14px;
  margin-top: 14px;
  padding: 11px 0;
  border-top: 1px solid ${({ theme }) => hairline(theme)};
  .k { min-width: 0; font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; }
  .k small { display: block; margin-top: 1px; font-size: 0.86em; font-weight: 400; letter-spacing: 0; color: ${({ theme }) => tokens.label2(theme)}; }
  .v { text-align: right; white-space: nowrap; font-size: 0.92em; font-weight: 600; letter-spacing: -0.02em; }
`;

const SpecSheet = styled.div`
  margin-top: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 24px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }

  .spec { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding: 10px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  .k { min-width: 0; font-size: 0.84em; font-weight: 500; letter-spacing: -0.01em; }
  .k small { display: block; margin-top: 1px; font-size: 0.86em; font-weight: 400; letter-spacing: 0; color: ${({ theme }) => tokens.label2(theme)}; }
  .v { white-space: nowrap; font-size: 0.92em; font-weight: 600; letter-spacing: -0.02em; }
`;

/* Número neutro, texto e um micrográfico em que só a parte que importa tem cor */
const InsightRow = styled.button`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 158px minmax(0, 1fr) 132px 14px;
  align-items: center;
  gap: 20px;
  padding: 16px 18px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text};
  font-family: inherit;
  font-size: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;
  transition: background 180ms ease;

  & + &::before { content: ""; position: absolute; top: 0; left: 18px; right: 0; height: 1px; background: ${({ theme }) => hairline(theme)}; }
  &:hover, &:focus-visible { background: ${({ theme }) => surface(theme, 1)}; outline: none; }
  &:hover .go, &:focus-visible .go { opacity: 0.5; transform: none; }
  &:hover .viz, &:focus-visible .viz { opacity: 1; }

  /* Mesma hierarquia dos valores em reais: número em destaque, moeda e unidade discretas */
  .metric { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .figure { display: flex; align-items: baseline; white-space: nowrap; font-size: 1.72em; font-weight: 600; letter-spacing: -0.04em; line-height: 1.05; }
  .figure .sign { margin-right: 1px; font-weight: 500; }
  .figure .cur { margin-right: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .figure .unit { margin-left: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .figure.word { font-size: 1.12em; font-weight: 500; letter-spacing: -0.02em; line-height: 1.3; color: ${({ theme }) => tokens.label2(theme)}; }
  .cap { font-size: 0.72em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: ${({ theme }) => tokens.label3(theme)}; }
  .text { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
  .t { font-size: 0.86em; font-weight: 450; letter-spacing: -0.008em; line-height: 1.3; color: ${({ theme }) => tokens.label(theme)}; }
  .s { font-size: 0.76em; font-weight: 300; line-height: 1.45; color: ${({ theme }) => tokens.label2(theme)}; }
  .viz { opacity: 0.88; transition: opacity 180ms ease; }
  .go { opacity: 0; transform: translateX(-3px); transition: opacity 180ms ease, transform 180ms ease; }

  @media (max-width: 900px) {
    grid-template-columns: 124px minmax(0, 1fr);
    .viz, .go { display: none; }
    .figure { font-size: 1.4em; }
  }
`;

export const CardRow = styled(InsightRow)`
  width: calc(100% + 16px);
  margin: 0 -8px;
  grid-template-columns: 132px minmax(0, 1fr) 14px;
  gap: 16px;
  padding: 13px 8px;
  border-radius: 12px;

  & + &::before { left: 8px; right: 8px; }
  .figure { font-size: 1.5em; }

  @media (max-width: 900px) { grid-template-columns: 112px minmax(0, 1fr); .figure { font-size: 1.3em; } }
`;

const MiniBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 30px;
  span { flex: 1; min-height: 3px; border-radius: 2px 2px 1px 1px; background: ${({ theme }) => tokens.label3(theme)}; opacity: 0.55; }
  span[style*="background"] { opacity: 1; }
`;

const MiniCompare = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  .l { display: grid; grid-template-columns: 30px minmax(0, 1fr); align-items: center; gap: 6px; font-size: 0.66em; color: ${({ theme }) => tokens.label3(theme)}; }
  .t { height: 4px; }
  .t i { display: block; height: 100%; min-width: 3px; border-radius: 2px; background: ${({ theme }) => tokens.label3(theme)}; }
`;

function Evolution({ data }) {
  const theme = useTheme();
  const axis = { tick: { fontSize: 10, fill: tokens.label2(theme) }, axisLine: false, tickLine: false };
  const { years, biggestJump } = data;
  return (
    <Widget $span={3} $delay={150}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
        <div>
          <div className="w-title">Evolução da carga</div>
          <div className="w-sub">
            {biggestJump
              ? <>Maior salto: <strong style={{ color: tokens.label(theme) }}>{biggestJump.ano}</strong> · {signed(biggestJump.delta)} sobre o ano anterior</>
              : "CBS e IBS crescem enquanto o regime vigente sai"}
          </div>
        </div>
        <div className="w-eyebrow" style={{ display: "flex", gap: 14 }}>
          {SERIES.map((s) => (
            <span key={s.key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.color }} />{s.label}
            </span>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <AreaChart data={years} margin={{ top: 10, right: 4, left: 0, bottom: 0 }}>
          <defs>
            {SERIES.map((s) => (
              <linearGradient key={s.key} id={`rf-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} stroke={tokens.separator(theme)} />
          <XAxis dataKey="label" {...axis} />
          <YAxis width={70} tickFormatter={formatCompact} {...axis} />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: tokens.label3(theme), strokeDasharray: "3 3" }} />
          {SERIES.map((s) => (
            <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stackId="1" stroke={s.color} strokeWidth={1.5} fill={`url(#rf-${s.key})`} isAnimationActive={false} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Widget>
  );
}

function Composition({ rows, total }) {
  const theme = useTheme();
  return (
    <CompositionWidget $delay={200}>
      <div className="w-title">Composição</div>
      <div className="w-sub">Carga atual, por tributo</div>
      <div style={{ position: "relative", height: 150, marginTop: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={rows} dataKey="chart" nameKey="label" innerRadius={56} outerRadius={68} cornerRadius={4} paddingAngle={3} startAngle={90} endAngle={-270} stroke="none" isAnimationActive={false}>
              {rows.map((r) => <Cell key={r.key} fill={r.color} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", fontWeight: 600, letterSpacing: "-0.02em" }}>
          {formatCompact(total)}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
        {rows.map((r) => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.8em" }} title={formatCurrency(r.value)}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 500 }}>{r.label}</span>
            <span style={{ color: tokens.label2(theme) }}>{formatPercent(r.share)}</span>
          </div>
        ))}
      </div>
    </CompositionWidget>
  );
}

function TransitionHeatmap({ data, onOpen }) {
  const theme = useTheme();
  const { years, biggestJump, turningYear } = data;
  const short = (v) => formatCompact(v).replace("R$", "").trim();
  const max = Math.max(1, ...years.flatMap((y) => [y.vigente, y.futuro]));
  const lines = [
    { key: "vigente", label: "Vigente", color: SERIES[0].color },
    { key: "futuro", label: "CBS + IBS", color: SERIES[2].color },
  ];
  return (
    <Widget $span={2} $delay={250} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div>
          <div className="w-title">Mapa da transição</div>
          <div className="w-sub">Carga estimada por ano, de 2026 a 2033. Clique para ver a transição</div>
        </div>
        <FiCalendar style={{ color: tokens.label3(theme) }} />
      </div>
      <MapScroll>
      <TransitionMap>
        <div />
        {years.map((y) => <div key={y.ano} className="head">{y.ano}</div>)}
        {lines.map((line) => (
          <React.Fragment key={line.key}>
            <div className="year">{line.label}</div>
            {years.map((y) => (
              <button key={y.ano} type="button" className="cell" title={`${line.label} em ${y.ano}: ${formatCurrency(y[line.key])}`} onClick={onOpen}>
                {y[line.key] > 0 && <span className="fill" style={{ background: line.color, opacity: 0.12 + (y[line.key] / max) * 0.5 }} />}
                <span className="txt">{short(y[line.key])}</span>
              </button>
            ))}
          </React.Fragment>
        ))}
        <div className="year">Total</div>
        {years.map((y) => <div key={y.ano} className="total" title={formatCurrency(y.total)}>{short(y.total)}</div>)}
      </TransitionMap>
      </MapScroll>
      <PeriodStats>
        <div>
          <span className="v">{biggestJump ? biggestJump.ano : "—"}</span>
          <span className="l">Maior salto{biggestJump ? ` · ${signed(biggestJump.delta)}` : ""}</span>
        </div>
        <div>
          <span className="v">{data.turningYear || "—"}</span>
          <span className="l">{turningYear ? "CBS e IBS passam o regime vigente" : "Sem virada no período"}</span>
        </div>
        <div>
          <span className="v" title={formatCurrency(data.future.total)}>{formatCompact(data.future.total)}</span>
          <span className="l">Regime pleno, em 2033</span>
        </div>
      </PeriodStats>
    </Widget>
  );
}

function AccountsBento({ credits, onOpen }) {
  const sum = credits.accounts.reduce((acc, c) => acc + c.total, 0);
  const top = credits.accounts.slice(0, 5);
  const others = credits.accounts.slice(5).reduce((acc, c) => acc + c.total, 0);
  const rows = [...top, ...(others > 0 ? [{ key: "Demais naturezas", total: others }] : [])]
    .map((r, i) => ({ ...r, share: sum > 0 ? (r.total / sum) * 100 : 0, color: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length], Icon: ACCOUNT_ICONS[r.code] || FiLayers }));
  const [first, second, ...rest] = rows;

  const tile = (r, size) => (
    <BentoTile key={r.key} type="button" $color={r.color} $size={size} title={`${r.key}: ${formatCurrency(r.total)}`} onClick={onOpen}>
      <span className="top">
        <span className="pct">{formatPercent(r.share).replace("%", "")}<small>%</small></span>
        <span className="icon"><r.Icon /></span>
      </span>
      <span className="bottom">
        <span className="label">{r.key}</span>
        <span className="amount">{size === "small" ? formatCompact(r.total) : <MoneyValue value={r.total} />}</span>
        <span className="track"><span className="level" style={{ width: `${r.share}%` }} /></span>
      </span>
    </BentoTile>
  );

  return (
    <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Despesas elegíveis</div>
          <div className="w-sub">{formatCompact(credits.base)} com crédito de CBS e IBS · SPED Contribuições (M105)</div>
        </div>
        <HeaderLink type="button" onClick={onOpen}>Ver todas <FiChevronRight /></HeaderLink>
      </div>
      {first && (
        <Bento>
          {tile(first, "large")}
          {second && (
            <div className="side">
              {tile(second, "medium")}
              {rest.length > 0 && <div className="base">{rest.map((r) => tile(r, "small"))}</div>}
            </div>
          )}
        </Bento>
      )}
    </Widget>
  );
}

function HighlightCard({ title, sub, linkLabel, onLink, headline, headlineColor, headlineLabel, segments, rest, delay, children }) {
  return (
    <Widget $delay={delay}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">{title}</div>
          {sub && <div className="w-sub">{sub}</div>}
        </div>
        {onLink && <HeaderLink type="button" onClick={onLink}>{linkLabel} <FiChevronRight /></HeaderLink>}
      </div>
      <TopHeadline>
        <span className="n" style={{ color: headlineColor }}>{headline}</span>
        <span className="l">{headlineLabel}</span>
      </TopHeadline>
      <StorageBar role="img" aria-label={`${headline} ${headlineLabel}`}>
        {segments.map((s) => <span key={s.key} title={s.label} style={{ flexGrow: Math.max(s.share, 0.5), background: s.color }} />)}
        {rest > 0 && <span className="rest" style={{ flexGrow: rest }} />}
      </StorageBar>
      <div style={{ marginTop: 8 }}>{children}</div>
    </Widget>
  );
}

function FigureRow({ figure, caption, title, sub, onClick }) {
  return (
    <CardRow type="button" onClick={onClick}>
      <span className="metric"><Figure {...figure} /><span className="cap">{caption}</span></span>
      <span className="text">
        <span className="t">{title}</span>
        <span className="s">{sub}</span>
      </span>
      <FiChevronRight className="go" />
    </CardRow>
  );
}

function CreditsCard({ credits, onOpen }) {
  const total = credits.rateCbs + credits.rateIbs;
  return (
    <HighlightCard
      title="Oportunidade de créditos"
      linkLabel="Ver detalhes"
      onLink={onOpen}
      delay={350}
      headline={formatCompact(credits.total)}
      headlineLabel={`de crédito sobre as despesas, ${formatPercent(total)} da base`}
      segments={[
        { key: "cbs", share: credits.rateCbs, color: SERIES[1].color, label: `CBS: ${formatPercent(credits.rateCbs)}` },
        { key: "ibs", share: credits.rateIbs, color: SERIES[2].color, label: `IBS: ${formatPercent(credits.rateIbs)}` },
      ]}
      rest={100 - total}
    >
      <FigureRow figure={{ money: credits.cbs, plain: true }} caption={`alíquota de ${formatPercent(credits.rateCbs)}`} title="Crédito CBS" sub="Federal" onClick={onOpen} />
      <FigureRow figure={{ money: credits.ibs, plain: true }} caption={`alíquota de ${formatPercent(credits.rateIbs)}`} title="Crédito IBS" sub="Estimado" onClick={onOpen} />
      <FigureRow
        figure={{ money: credits.gain, color: IOS.green }}
        caption="sobre o regime atual"
        title="Ganho vs. regime atual"
        sub={`Frente ao PIS/COFINS de 9,25%${credits.todayIsEstimated ? ", estimado sem o M505" : ""}`}
        onClick={onOpen}
      />
    </HighlightCard>
  );
}

function CashCard({ cash, onOpen }) {
  return (
    <HighlightCard
      title="Caixa fiscal"
      linkLabel="Ver caixa"
      onLink={onOpen}
      delay={400}
      headline={formatTimes(cash.multiplier)}
      headlineLabel="o volume bruto de hoje, o que exige gestão ativa"
      segments={[
        { key: "credit", share: cash.coverageFuture, color: SERIES[2].color, label: `Coberto por crédito em 2033: ${formatPercent(cash.coverageFuture)}` },
        { key: "net", share: Math.max(0, 100 - cash.coverageFuture), color: IOS.red, label: "Pagamento líquido" },
      ]}
      rest={0}
    >
      <FigureRow figure={{ money: cash.debitFutureMonth, plain: true }} caption="por mês" title="Débito bruto futuro" sub={`${formatTimes(cash.multiplier)} o atual, de ${formatCompact(cash.debitTodayMonth)}`} onClick={onOpen} />
      <FigureRow figure={{ money: cash.creditFutureMonth, plain: true }} caption="por mês" title="Créditos em trânsito" sub="Em risco se impugnados" onClick={onOpen} />
      <FigureRow figure={{ money: cash.netFutureMonth, plain: true }} caption="por mês" title="Líquido futuro" sub={`${formatCompact(cash.netTodayMonth)} atual. Média: total ÷ 12`} onClick={onOpen} />
    </HighlightCard>
  );
}

export function RateDumbbell({ from, to, color, scale, format = formatPercent }) {
  const max = scale || Math.max(10, Math.ceil(Math.max(from, to) / 5) * 5);
  const x = (v) => (Math.max(0, v) / max) * 100;
  const left = Math.min(x(from), x(to));
  const width = Math.abs(x(to) - x(from));
  return (
    <Dumbbell role="img" aria-label={`De ${format(from)} hoje para ${format(to)} em 2033`}>
      <span className="rail" />
      <span className="span" style={{ left: `${left}%`, width: `${width}%`, background: `linear-gradient(to right, ${color}26, ${color})` }} />
      <span className="pt" style={{ left: `${x(from)}%`, background: IOS.gray }} />
      <span className="pt" style={{ left: `${x(to)}%`, background: color }} />
      <span className="lb from" style={{ left: `${x(from)}%` }}>{format(from)}</span>
      <span className="lb" style={{ left: `${x(to)}%`, color }}>{format(to)}</span>
    </Dumbbell>
  );
}

function SegmentsCard({ segments, workshop, onOpen }) {
  const max = Math.max(1, ...segments.map((s) => Math.abs(s.delta)));
  return (
    <Widget $delay={450} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Impacto por segmento</div>
          <div className="w-sub">Estimativa à parte, sem o crédito sobre despesas. Não soma ao impacto total</div>
        </div>
        <HeaderLink type="button" onClick={onOpen}>Ver caixa <FiChevronRight /></HeaderLink>
      </div>
      <PlotHead aria-hidden="true">
        <span>Segmento</span>
        <span className="dir"><span>menos imposto</span><span>mais imposto</span></span>
        <span className="end">Variação</span>
      </PlotHead>
      <div role="img" aria-label="Variação estimada da carga por segmento">
        {segments.map((s) => {
          const ratio = Math.abs(s.delta) / max;
          const color = directionColor(s.delta);
          const up = s.delta >= 0;
          // A cor ganha força com o peso: o maior segmento é o único em tom pleno
          const strength = Math.round((0.28 + ratio * 0.72) * 255).toString(16).padStart(2, "0");
          return (
            <PlotRow key={s.key}>
              <div className="name">{s.label}<small>{s.share}</small></div>
              <div className="plot">
                <i
                  className={up ? "up" : "down"}
                  style={{
                    width: `${ratio * 50}%`,
                    left: up ? "50%" : `${50 - ratio * 50}%`,
                    background: `linear-gradient(to ${up ? "right" : "left"}, ${color}33, ${color}${strength})`,
                  }}
                />
              </div>
              <div className="num" title={formatCurrency(s.delta)}>{signed(s.delta)}</div>
            </PlotRow>
          );
        })}
      </div>
      <WorkshopRate>
        <div className="name">Alíquota dos serviços<small>Mão de obra não gera crédito</small></div>
        <RateDumbbell from={workshop.rateToday} to={workshop.rateFuture} color={directionColor(workshop.rateFuture - workshop.rateToday)} />
        <div className="num" title={formatCurrency(workshop.delta)}>{formatTimes(workshop.rateToday ? workshop.rateFuture / workshop.rateToday : 0)}<small>hoje → 2033</small></div>
      </WorkshopRate>
    </Widget>
  );
}

function BaseCard({ data, isIndustrial, onOpen }) {
  const a = data.assumptions;
  const fromSped = data.current.total > 0 ? (data.current.fromSped / data.current.total) * 100 : 0;
  const facts = [
    { k: "CBS plena", v: formatPercent(a.cbs), hint: "Federal" },
    { k: "IBS pleno", v: formatPercent(a.ibs), hint: "Estimado para SP" },
    { k: "1 ponto de custo", v: formatCompact(a.onePointOfCost), hint: "Efeito na carga de 2033" },
    { k: "Imposto Seletivo", v: isIndustrial ? "Pode se aplicar" : "Não se aplica", hint: isIndustrial ? "Verifique os NCMs" : "Só indústria e importador" },
  ];
  return (
    <Widget $delay={500} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Base do cálculo</div>
          <div className="w-sub">Sólido é dado do SPED. Hachurado é premissa</div>
        </div>
        <HeaderLink type="button" onClick={onOpen}>Editar parâmetros <FiChevronRight /></HeaderLink>
      </div>
      <SourceTrack role="img" aria-label={`${formatPercent(fromSped)} da carga atual vem do SPED`}>
        <span className="real" style={{ flexGrow: fromSped }} />
        <span className="est" style={{ flexGrow: 100 - fromSped }} />
      </SourceTrack>
      <SourceCaps>
        <span><b>{formatCompact(data.current.fromSped)}</b> PIS, COFINS e ICMS</span>
        <span><b>{formatCompact(data.current.iss)}</b> ISS estimado</span>
      </SourceCaps>
      <BulletRow>
        <div className="k">Custo das vendas<small>Entradas no SPED: {formatPercent(a.purchasesShare)}</small></div>
        <Bullet role="img" aria-label={`Premissa de ${formatPercent(a.costShare)}; entradas no SPED somam ${formatPercent(a.purchasesShare)} do faturamento`}>
          <i style={{ width: `${Math.min(100, a.costShare)}%` }} />
          <b style={{ left: `${Math.min(100, a.purchasesShare)}%` }} />
        </Bullet>
        <div className="v">{formatPercent(a.costShare)}</div>
      </BulletRow>
      <SpecSheet>
        {facts.map((f) => (
          <div className="spec" key={f.k}>
            <div className="k">{f.k}<small>{f.hint}</small></div>
            <div className="v">{f.v}</div>
          </div>
        ))}
      </SpecSheet>
    </Widget>
  );
}

export function Compare({ today, future, color }) {
  const max = Math.max(Math.abs(today), Math.abs(future), 1);
  return (
    <MiniCompare className="viz" aria-hidden="true">
      <div className="l"><span>Hoje</span><span className="t"><i style={{ width: `${(Math.abs(today) / max) * 100}%` }} /></span></div>
      <div className="l"><span>2033</span><span className="t"><i style={{ width: `${(Math.abs(future) / max) * 100}%`, background: color }} /></span></div>
    </MiniCompare>
  );
}

function YearBars({ years, highlight, color }) {
  const totals = years.map((y) => y.total);
  const low = Math.min(...totals) * 0.72;
  const high = Math.max(...totals);
  return (
    <MiniBars className="viz" aria-hidden="true">
      {years.map((y) => (
        <span key={y.ano} style={{ height: `${((y.total - low) / (high - low || 1)) * 100}%`, background: y.ano === highlight ? color : undefined }} />
      ))}
    </MiniBars>
  );
}

// "+R$ 2,8 mi" vira sinal, moeda, número e unidade; só o sinal leva a cor do sentido
export function Figure({ money, percent, times, word, color, plain }) {
  if (word) return <span className="figure word">{word}</span>;
  if (times !== undefined) {
    return <span className="figure">{formatTimes(times).replace("×", "")}<span className="unit">×</span></span>;
  }
  if (percent !== undefined) {
    return (
      <span className="figure">
        <span className="sign" style={{ color }}>{percent >= 0 ? "+" : "−"}</span>
        {Math.abs(percent).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}<span className="unit">%</span>
      </span>
    );
  }
  const [, number = "0", unit = ""] = formatCompact(Math.abs(money)).match(/R\$\s*([\d.,]+)\s*(.*)/) || [];
  return (
    <span className="figure" title={formatCurrency(money)}>
      {!plain && <span className="sign" style={{ color }}>{money >= 0 ? "+" : "−"}</span>}
      <span className="cur">R$</span>{number}{unit && <span className="unit">{unit}</span>}
    </span>
  );
}

function Insights({ data, hasEcd, onGo }) {
  const { biggestJump, workshop, cash, credits, years } = data;
  const rows = [
    biggestJump && {
      key: "transition", view: "transicao-anual", figure: { money: biggestJump.delta, color: directionColor(biggestJump.delta) }, caption: `de ${biggestJump.ano - 1} para ${biggestJump.ano}`,
      title: `O maior salto vem em ${biggestJump.ano}`,
      sub: "A carga sobe de um ano para o outro antes do regime pleno de 2033.",
      viz: <YearBars years={years} highlight={biggestJump.ano} color={directionColor(biggestJump.delta)} />,
    },
    {
      key: "cash", view: "impacto-caixa", figure: { times: cash.multiplier }, caption: "débito bruto vs. hoje",
      title: "Volume bruto muda de escala: gestão ativa é recomendada",
      sub: `Débitos brutos mensais vão de ${formatCompact(cash.debitTodayMonth)} para ${formatCompact(cash.debitFutureMonth)}. O fluxo de caixa exige acompanhar os créditos em trânsito.`,
      viz: <Compare today={cash.debitTodayMonth} future={cash.debitFutureMonth} color={IOS.blue} />,
    },
    {
      key: "workshop", view: "impacto-caixa", figure: { money: workshop.delta, color: directionColor(workshop.delta) }, caption: "nos serviços",
      title: "Serviços são o ponto crítico: mão de obra não gera crédito CBS/IBS",
      sub: "Os serviços concentram o maior impacto negativo da transição. O detalhamento está na aba Caixa.",
      viz: <Compare today={workshop.rateToday} future={workshop.rateFuture} color={directionColor(workshop.delta)} />,
    },
    {
      key: "credits", view: "creditos-despesas", figure: { money: credits.gain, color: IOS.green }, caption: "em crédito, vs. PIS/COFINS",
      title: "Ganho de crédito sobre despesas operacionais",
      sub: "O novo crédito amplo de CBS/IBS sobre despesas elegíveis frente ao PIS/COFINS atual.",
      viz: <Compare today={credits.total - credits.gain} future={credits.total} color={IOS.green} />,
    },
    !hasEcd && {
      key: "ecd", view: "parametros", figure: { word: "Pendente" }, caption: "ação no projeto",
      title: "ECD não importada",
      sub: "Ela traz as contas de despesa usadas na formação de preço. Os detalhes estão em Parâmetros.",
      viz: <span />,
    },
  ].filter(Boolean);

  return (
    <Section $delay={240}>
      <SectionHeader><h2>Insights executivos</h2></SectionHeader>
      <Group>
        {rows.map((r) => (
          <InsightRow key={r.key} type="button" onClick={() => onGo(r.view)}>
            <span className="metric"><Figure {...r.figure} /><span className="cap">{r.caption}</span></span>
            <span className="text">
              <span className="t">{r.title}</span>
              <span className="s">{r.sub}</span>
            </span>
            {r.viz}
            <FiChevronRight className="go" />
          </InsightRow>
        ))}
      </Group>
    </Section>
  );
}

export function ReformaDashboardSkeleton() {
  return (
    <Section $first>
      <WidgetGrid>{[0, 1, 2, 3].map((i) => <Widget key={i}><Skeleton $h={78} $r={10} /></Widget>)}</WidgetGrid>
      <div style={{ height: 14 }} />
      <Skeleton $h={300} $r={16} />
    </Section>
  );
}

export default function ReformaDashboard({ fiscalData, params, isIndustrial, hasEcd, companyFiltered, onGo }) {
  const data = useMemo(() => buildReformaDashboard(fiscalData, params), [fiscalData, params]);
  const { current, future, impact, years, months } = data;

  return (
    <>
      <Section $first>
        {companyFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Com empresa selecionada, a comparação mistura bases: PIS/COFINS é apurado na matriz, e as despesas (M105) são do projeto inteiro.</span>
          </Notice>
        )}
        <WidgetGrid>
          <Widget $delay={0} $clickable onClick={() => onGo("dados-mensais")}>
            <div className="w-head">{dot(IOS.mint)} Faturamento</div>
            <div className="w-value"><MoneyValue value={data.faturamento} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{data.monthCount} {data.monthCount === 1 ? "mês" : "meses"}</span>
              <Sparkline data={months.map((m) => m.faturamento)} color={IOS.mint} {...spark} />
            </div>
          </Widget>
          <Widget $delay={50} $clickable onClick={() => onGo("simulacao")}>
            <div className="w-head">{dot(SERIES[0].color)} Carga atual</div>
            <div className="w-value"><MoneyValue value={current.total} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatRate(current.rate)} do faturamento</span>
              <Sparkline data={months.map((m) => m.carga)} color={SERIES[0].color} {...spark} />
            </div>
          </Widget>
          <Widget $delay={100} $clickable onClick={() => onGo("simulacao")}>
            <div className="w-head">{dot(SERIES[1].color)} Carga futura</div>
            <div className="w-value"><MoneyValue value={future.total} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span>{formatRate(future.rate)} em 2033</span>
              <Sparkline data={years.map((y) => y.futuro)} color={SERIES[1].color} {...spark} />
            </div>
          </Widget>
          <Widget $delay={150} $clickable onClick={() => onGo("transicao-anual")}>
            <div className="w-head">{dot(directionColor(impact.value))} Impacto líquido</div>
            <div className="w-value"><MoneyValue value={impact.value} /></div>
            <div className="w-foot" style={{ justifyContent: "space-between" }}>
              <span style={{ color: directionColor(impact.value) }}>{impact.value > 0 ? "+" : ""}{formatPercent(impact.percent)}</span>
              <Sparkline data={years.map((y) => y.total)} color={directionColor(impact.value)} {...spark} />
            </div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Evolution data={data} />
          <Composition rows={data.composition} total={current.total} />
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <TransitionHeatmap data={data} onOpen={() => onGo("transicao-anual")} />
          <AccountsBento credits={data.credits} onOpen={() => onGo("creditos-despesas")} />
        </WidgetGrid>
      </Section>

      <Section $delay={200}>
        <WidgetGrid $cols={2}>
          <CreditsCard credits={data.credits} onOpen={() => onGo("creditos-despesas")} />
          <CashCard cash={data.cash} onOpen={() => onGo("impacto-caixa")} />
          <SegmentsCard segments={data.segments} workshop={data.workshop} onOpen={() => onGo("impacto-caixa")} />
          <BaseCard data={data} isIndustrial={isIndustrial} onOpen={() => onGo("parametros")} />
        </WidgetGrid>
      </Section>

      <Insights data={data} hasEcd={hasEcd} onGo={onGo} />
    </>
  );
}
